import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-gas-costs-averages',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './gas-costs-averages.component.html',
  styleUrl: './gas-costs-averages.component.css'
})
export class GasCostsAveragesComponent implements OnInit, OnChanges {
  data: any;
  options: any;
  labels: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummyaveragePricePerSmcData = [130, 110, 138, 135, 140, 142, 135, 130, 145, 152, 20, 140];
  dummyForecastedStartMonth = 6;

  averagePricePerSmcActualLabel: string = "Average Price per smc ( Actual )";
  averagePricePerSmcForecastLabel: string = "Average Price per smc ( Forecast )";

  @Input() averagePricePerSmcData: (number | null)[] = [130, 110, 138, 135, 140, 142, 135, 130, 145, 152, 20, 140];
  @Input() forecastedStartMonth: number | null = null;

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart()
  }

  ngOnInit(): void {
    this.updateChart();

    combineLatest([
      this.translocoService.selectTranslate('dashboard.average_price_per_smc_actual_label'),
      this.translocoService.selectTranslate('dashboard.average_price_per_smc_forecast_label'),
    ]).subscribe(([averagePricePerSmcActualTranslation, averagePricePerSmcForecastTranslation]) => {
      this.averagePricePerSmcActualLabel = averagePricePerSmcActualTranslation;
      this.averagePricePerSmcForecastLabel = averagePricePerSmcForecastTranslation;
    });

    const monthKeys = [
      'shared.months.jan',
      'shared.months.feb',
      'shared.months.mar',
      'shared.months.apr',
      'shared.months.may',
      'shared.months.jun',
      'shared.months.jul',
      'shared.months.aug',
      'shared.months.sep',
      'shared.months.oct',
      'shared.months.nov',
      'shared.months.dec',
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.labels = translations;
      this.updateChart();
    });
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.averagePricePerSmcData = this.dummyaveragePricePerSmcData;
      this.forecastedStartMonth = this.dummyForecastedStartMonth;
    }

    Chart.register(ChartDataLabels);
    this.data = {
      labels: this.labels,
      datasets: [
        {
          //label: this.averagePricePerSmcActualLabel,
          data: this.averagePricePerSmcData,
          borderColor: '#80a7a5',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 5,
          pointBackgroundColor: '#80a7a5',
          segment: {
            borderDash: (ctx: any) => {
              const index = ctx.p0DataIndex;
              if (this.forecastedStartMonth === null) {
                return undefined;
              }
              return index >= this.forecastedStartMonth ? [5, 5] : undefined;
            },
          },
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: this.isMobile ? 0 : 50,
        }
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          align: 'end',
          anchor: 'end',
          clamp: true,
          clip: false,
          display: 'auto',
          formatter: (_value: any, context: any) => {
            return `${this.decimalPipe.transform(_value, '1.2-2') || _value}€/smc`;
          },
          color: "#000000",
          font: {
            size: 10,
            weight: 'normal',
            family: 'Gilroy',
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const index = context.dataIndex;
              var datasetLabel = this.averagePricePerSmcActualLabel;
              if (this.forecastedStartMonth) {
                datasetLabel =
                  index >= this.forecastedStartMonth
                    ? this.averagePricePerSmcForecastLabel
                    : this.averagePricePerSmcActualLabel;
              }

              const formattedValue = parseFloat(context.raw).toFixed(2);
              return `${datasetLabel}: ${this.decimalPipe.transform(formattedValue, '1.2-2') || formattedValue} EUR/smc`;
            }
          }
        }

      },
      scales: {
        x: {
          display: false,
          offset: true,
        },
        y: {
          display: false,
          beginAtZero: true,
        },
      },
    };
  }
}
